import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { ethers } from 'ethers';

const Web3Context = createContext(null);

export const Web3Provider = ({ children }) => {
  const [account, setAccount] = useState(null);
  const [provider, setProvider] = useState(null);
  const [signer, setSigner] = useState(null);
  const [balance, setBalance] = useState('0');
  const [chainId, setChainId] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState(null);
  const [walletType, setWalletType] = useState(null);

  // Check for existing connection on mount
  useEffect(() => {
    const savedWallet = localStorage.getItem('aih_wallet_type');
    const savedAccount = localStorage.getItem('aih_wallet_account');
    
    if (savedWallet && savedAccount && window.ethereum) {
      reconnectWallet(savedWallet);
    }
  }, []);

  // Listen for account and chain changes
  useEffect(() => {
    if (window.ethereum) {
      window.ethereum.on('accountsChanged', handleAccountsChanged);
      window.ethereum.on('chainChanged', handleChainChanged);
      
      return () => {
        window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
        window.ethereum.removeListener('chainChanged', handleChainChanged);
      };
    }
  }, []);

  const handleAccountsChanged = async (accounts) => {
    if (accounts.length === 0) {
      disconnectWallet();
    } else {
      setAccount(accounts[0]);
      localStorage.setItem('aih_wallet_account', accounts[0]);
      if (provider) {
        const bal = await provider.getBalance(accounts[0]);
        setBalance(ethers.formatEther(bal));
      }
    }
  };

  const handleChainChanged = (newChainId) => {
    setChainId(parseInt(newChainId, 16));
    window.location.reload();
  };

  const reconnectWallet = async (wallet) => {
    try {
      if (wallet === 'metamask' && window.ethereum) {
        const accounts = await window.ethereum.request({ method: 'eth_accounts' });
        if (accounts.length > 0) {
          await connectWallet('metamask');
        }
      }
    } catch (err) {
      console.error('Failed to reconnect wallet:', err);
    }
  };

  const connectWallet = useCallback(async (wallet) => {
    setIsConnecting(true);
    setError(null);

    try {
      if (wallet === 'metamask') {
        if (!window.ethereum) {
          throw new Error('MetaMask is not installed. Please install MetaMask to continue.');
        }

        const browserProvider = new ethers.BrowserProvider(window.ethereum);
        const accounts = await browserProvider.send('eth_requestAccounts', []);
        
        if (accounts.length === 0) {
          throw new Error('No accounts found. Please unlock MetaMask.');
        }

        const walletSigner = await browserProvider.getSigner();
        const network = await browserProvider.getNetwork();
        const userBalance = await browserProvider.getBalance(accounts[0]);
        
        setAccount(accounts[0]);
        setProvider(browserProvider);
        setSigner(walletSigner);
        setChainId(Number(network.chainId));
        setBalance(ethers.formatEther(userBalance));
        setWalletType('metamask');
        
        localStorage.setItem('aih_wallet_type', 'metamask');
        localStorage.setItem('aih_wallet_account', accounts[0]);
      } else if (wallet === 'unisat') {
        // Bitcoin wallet - Unisat
        if (!window.unisat) {
          throw new Error('Unisat wallet is not installed. Please install Unisat to continue.');
        }
        
        const accounts = await window.unisat.requestAccounts();
        if (accounts.length === 0) {
          throw new Error('No accounts found in Unisat wallet.');
        }
        
        const btcBalance = await window.unisat.getBalance();
        
        setAccount(accounts[0]);
        setBalance((btcBalance.confirmed / 100000000).toString());
        setWalletType('unisat');
        setChainId(0); // Bitcoin mainnet indicator
        
        localStorage.setItem('aih_wallet_type', 'unisat');
        localStorage.setItem('aih_wallet_account', accounts[0]);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to connect wallet';
      setError(errorMessage);
      console.error('Wallet connection error:', err);
    } finally {
      setIsConnecting(false);
    }
  }, []);

  const disconnectWallet = useCallback(() => {
    setAccount(null);
    setProvider(null);
    setSigner(null);
    setBalance('0');
    setChainId(null);
    setWalletType(null);
    setError(null);
    localStorage.removeItem('aih_wallet_type');
    localStorage.removeItem('aih_wallet_account');
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const signMessage = useCallback(async (message) => {
    if (!signer) {
      throw new Error('Wallet not connected');
    }
    return await signer.signMessage(message);
  }, [signer]);

  const sendTransaction = useCallback(async (to, value) => {
    if (!signer) {
      throw new Error('Wallet not connected');
    }
    
    const tx = {
      to,
      value: ethers.parseEther(value.toString()),
    };
    
    const transaction = await signer.sendTransaction(tx);
    return transaction;
  }, [signer]);

  const getNetworkName = (id) => {
    const networks = {
      1: 'Ethereum Mainnet',
      5: 'Goerli Testnet',
      11155111: 'Sepolia Testnet',
      137: 'Polygon Mainnet',
      80001: 'Mumbai Testnet',
      56: 'BSC Mainnet',
      43114: 'Avalanche C-Chain',
      42161: 'Arbitrum One',
      10: 'Optimism',
      0: 'Bitcoin Mainnet',
    };
    return networks[id] || `Chain ${id}`;
  };

  const value = {
    account,
    provider,
    signer,
    balance,
    chainId,
    isConnecting,
    error,
    walletType,
    isConnected: !!account,
    connectWallet,
    disconnectWallet,
    clearError,
    signMessage,
    sendTransaction,
    getNetworkName,
  };

  return (
    <Web3Context.Provider value={value}>
      {children}
    </Web3Context.Provider>
  );
};

export const useWeb3 = () => {
  const context = useContext(Web3Context);
  if (!context) {
    throw new Error('useWeb3 must be used within Web3Provider');
  }
  return context;
};
